import { guided } from './guidedRuntime';
import { GuidedLLMError } from './guidedClient';
import { PLAN_SYSTEM_PROMPT } from './prompt/plan-prompt';
import { parseModelJson } from './parser/balanceJson';
import type { Plan, LayoutOption, StyleBrief, NavKind, MainLayout, PatternId } from './planTypes';

const NAVS: NavKind[] = ['tabs', 'stack', 'single', 'drawer'];
const LAYOUTS: MainLayout[] = ['list', 'feed', 'grid', 'detail', 'form', 'dashboard'];
const isHex = (s: unknown): s is string => typeof s === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(s);

const RADII = ['sharp', 'soft', 'round'] as const;
const DENSITIES = ['tight', 'comfortable', 'airy'] as const;
const SCALES = ['restrained', 'bold', 'hero'] as const;
const PATTERNS: PatternId[] = ['two-tone-headings', 'floating-tabbar', 'icon-chips', 'segmented-range', 'gradient-chart', 'peek-carousel', 'glass-overlay', 'paired-stats'];
const pickEnum = <T extends string>(v: unknown, allowed: readonly T[], dflt: T): T => (allowed.includes(v as T) ? (v as T) : dflt);

export function normalizeStyle(raw: unknown): StyleBrief {
  const r = (raw ?? {}) as Record<string, unknown>;
  const p = (r.palette ?? {}) as Record<string, unknown>;
  return {
    palette: {
      primary: isHex(p.primary) ? p.primary : '#6C5CE7',
      accent: isHex(p.accent) ? p.accent : undefined,
      background: isHex(p.background) ? p.background : '#FFFFFF',
      text: isHex(p.text) ? p.text : '#1A1A1A',
    },
    mood: typeof r.mood === 'string' ? r.mood : '',
    keywords: Array.isArray(r.keywords) ? r.keywords.filter((k) => typeof k === 'string').slice(0, 6) : [],
    radius: pickEnum(r.radius, RADII, 'soft'),
    density: pickEnum(r.density, DENSITIES, 'comfortable'),
    typeScale: pickEnum(r.typeScale, SCALES, 'bold'),
    patterns: Array.isArray(r.patterns) ? (r.patterns.filter((x) => PATTERNS.includes(x as PatternId)) as PatternId[]) : [],
  };
}

function normalizeOption(raw: unknown, i: number): LayoutOption | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  if (typeof r.title !== 'string' && typeof r.blueprint !== 'string') return null;
  const nav = NAVS.includes(r.nav as NavKind) ? (r.nav as NavKind) : 'stack';
  const tabs = Array.isArray(r.tabs) ? r.tabs.filter((t) => typeof t === 'string').slice(0, 5) : undefined;
  // A screen arrives as `{label, purpose}` (what the prompt asks for) or as a bare name (what
  // it used to ask for, and what a model sometimes answers anyway). Both are kept.
  const screens: { label: string; purpose: string }[] = [];
  for (const s of Array.isArray(r.screens) ? r.screens : []) {
    const o = (s ?? {}) as Record<string, unknown>;
    const label = (typeof s === 'string' ? s : typeof o.label === 'string' ? o.label : '').trim();
    if (!label) continue;
    screens.push({ label, purpose: typeof o.purpose === 'string' ? o.purpose.trim() : '' });
    if (screens.length === 6) break;
  }
  return {
    id: typeof r.id === 'string' && r.id ? r.id : `opt-${i + 1}`,
    title: typeof r.title === 'string' ? r.title : `Option ${i + 1}`,
    blueprint: typeof r.blueprint === 'string' ? r.blueprint : (typeof r.title === 'string' ? r.title : ''),
    nav,
    tabs: nav === 'tabs' || nav === 'drawer' ? tabs : undefined,
    mainLayout: LAYOUTS.includes(r.mainLayout as MainLayout) ? (r.mainLayout as MainLayout) : 'list',
    screens: screens.map((x) => x.label),
    purposes: screens.map((x) => x.purpose),
  };
}

/** Parse the planning model's reply into a Plan, tolerating stray prose/fences. Returns
 *  null when there's no usable plan (caller falls back to a direct build). */
export function parsePlan(content: string): Plan | null {
  // Closes a root the chat model forgot to close — half of golf's layout replies. See balanceJson.
  const raw = parseModelJson(content ?? '') as Record<string, unknown> | null;
  if (!raw || typeof raw !== 'object') return null;
  if (!raw.styleBrief || !Array.isArray(raw.options)) return null;
  const options = raw.options.slice(0, 3).map(normalizeOption).filter((o): o is LayoutOption => !!o);
  if (!options.length) return null;
  return { styleBrief: normalizeStyle(raw.styleBrief), options };
}

/** Why no layouts came back — the wizard says a different thing for each. */
export type LayoutsMissing = 'signed-out' | 'no-credits' | 'failed';

export type LayoutsOutcome =
  | { kind: 'planned'; plan: Plan }
  | { kind: 'failed'; reason: LayoutsMissing };

export interface PlanLayoutsDeps {
  generate?: (input: {
    systemPrompt: string;
    messages: { role: 'user' | 'assistant'; content: string }[];
    signedIn: boolean;
  }) => Promise<{ content: string }>;
}

/**
 * Propose 2-3 layouts for the app as it now stands.
 *
 * NO `structured` flag, deliberately — the same choice `rankKitsFromFeedback` makes and for
 * the same reason. `/llm` prices a structured call as an EDIT (500 credits, taken before the
 * model runs), and only a `generation` is covered by the free first build. So this call, which
 * the wizard makes on every visit to the layout step, cost a new account 500 of its 714 free
 * credits while the note under it said "This one is free" — and left too little for a single
 * change after the free build. The owner's unlimited account could never have shown it.
 *
 * Unflagged, it runs on the chat model at the chat cap and is priced at nothing. That cap is
 * ample: a full reply (style brief plus three options with a purpose per screen) is about 500
 * tokens against 1,500. A layout proposal is an opinion about arrangement; paying build-model
 * rates for it would be spending the person's credits on an opinion.
 *
 * The failure carries a REASON because the wizard must not say "just now" about a failure
 * that will happen every time: signed out (`/llm` refuses anonymous callers) or out of credits.
 */
export async function planLayouts(
  input: { goal: string; usage?: string },
  deps: PlanLayoutsDeps = {},
): Promise<LayoutsOutcome> {
  const user = `App goal: ${input.goal}` +
    (input.usage ? `\nHow people will use it: ${input.usage}` : '') +
    `\n\nReturn the JSON plan now.`;
  let content: string;
  try {
    const generate = deps.generate ?? ((req) => guided().generate(req));
    const res = await generate({
      systemPrompt: PLAN_SYSTEM_PROMPT,
      messages: [{ role: 'user', content: user }],
      signedIn: true,
    });
    content = res.content ?? '';
  } catch (e) {
    const reason = e instanceof GuidedLLMError ? e.reason : 'unknown';
    if (reason === 'signup_required') return { kind: 'failed', reason: 'signed-out' };
    if (reason === 'insufficient_credits') return { kind: 'failed', reason: 'no-credits' };
    return { kind: 'failed', reason: 'failed' };
  }
  const plan = parsePlan(content);
  return plan ? { kind: 'planned', plan } : { kind: 'failed', reason: 'failed' };
}
